import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Translate } from 'react-localize-redux';
import DropdownField from 'components/molecules/DropdownField';
import { OTHER_PLATFORM, WEB_PLATFORM } from '../../constants';
import { getPublicIdForDisplay } from '../../utils';
import { getOtherPlatformDropdownItems, getOtherPlatformOptions, MANUAL_PUBLIC_ID_PLATFORMS } from '../appCardUtils';
import { Field, HelpLabel, StaticMetric } from '../controls';
import css from '../AppCard.css';
import { StoreUrlField } from './fields';
import { platformPropType, sitePropType } from './propTypes';

const SiteIdentityFields = ({
    site,
    displayAsPublished,
    platformName,
    platforms,
    readOnly,
    showManualPublicId,
    showMappingKey,
    showOtherLatestSdkVersion,
    showOtherPlatformSelector,
    showSdkNotIntegrated,
    showSdkVersion,
    storeUrlDraft,
    validationErrors,
    onSitePatch,
    onStoreUrlDraftChange,
}) => {
    const otherPlatformOptions = getOtherPlatformOptions(platforms);
    const otherPlatformDropdownItems = getOtherPlatformDropdownItems(otherPlatformOptions);

    return (
        <div
            className={classNames(css.formGridWide, {
                [css.formGridWideNoMapping]: !showMappingKey,
                [css.formGridWideManual]: !displayAsPublished,
                [css.formGridWideOther]: showOtherPlatformSelector,
                [css.formGridWideOtherWithSdk]: showOtherLatestSdkVersion,
                [css.formGridWideWeb]:
                    displayAsPublished && !showOtherPlatformSelector && platformName === WEB_PLATFORM,
            })}
        >
            {showOtherPlatformSelector ? (
                <>
                    <DropdownField
                        label="STATIC.PAGES.APPS_CONFIGURATION.PLATFORM_SELECTION_LABEL"
                        labelTooltip="STATIC.PAGES.APPS_CONFIGURATION.PLACEHOLDERS.CHOOSE_PLATFORM"
                        selected={site.platform?.name || ''}
                        items={otherPlatformDropdownItems}
                        disabled={readOnly}
                        onChange={selectedName => {
                            const selectedPlatform = otherPlatformOptions.find(
                                platform => platform.name === selectedName
                            );
                            onSitePatch({ platform: selectedPlatform || site.platform });
                        }}
                        containerClass={css.otherPlatformField}
                        dropdownContainerClassName={css.otherPlatformDropdown}
                        dropdownSelectedClassName={css.otherPlatformDropdownSelected}
                        bodyClassName={css.otherPlatformDropdownBody}
                        placement="bottom"
                        disableFlip
                    />
                    <StoreUrlField
                        label={<Translate id="STATIC.PAGES.APPS_CONFIGURATION.URL_INPUT_LABEL" />}
                        committedValue={site.url}
                        value={storeUrlDraft}
                        error={validationErrors.url}
                        disabled={readOnly}
                        onCommit={value => onSitePatch({ url: value })}
                        onDraftChange={onStoreUrlDraftChange}
                    />
                    <Field
                        label={
                            <HelpLabel
                                id="STATIC.PAGES.APPS_CONFIGURATION.BUNDLE_ID"
                                tooltip="STATIC.PAGES.APPS_CONFIGURATION.BUNDLE_ID_TOOLTIP_GENERIC"
                            />
                        }
                        value={site.longname}
                        error={validationErrors.longname}
                        disabled={readOnly}
                        onChange={value => onSitePatch({ longname: value })}
                    />
                    {showOtherLatestSdkVersion ? (
                        <StaticMetric
                            label={<Translate id="STATIC.PAGES.APPS_CONFIGURATION.LATEST_SDK_VERSION" />}
                            value={site.latest_sdk_version || '-'}
                        />
                    ) : null}
                </>
            ) : displayAsPublished ? (
                <StoreUrlField
                    label={
                        platformName === WEB_PLATFORM ? (
                            <Translate id="STATIC.PAGES.APPS_CONFIGURATION.URL_INPUT_LABEL" />
                        ) : (
                            <Translate id="STATIC.PAGES.APPS_CONFIGURATION.STORE_URL" />
                        )
                    }
                    committedValue={site.url}
                    value={storeUrlDraft || (MANUAL_PUBLIC_ID_PLATFORMS.includes(platformName) ? site.longname : '')}
                    error={validationErrors.url}
                    disabled={readOnly}
                    onCommit={value => onSitePatch({ url: value })}
                    onDraftChange={onStoreUrlDraftChange}
                />
            ) : (
                <Field
                    label={
                        <HelpLabel
                            id="STATIC.PAGES.APPS_CONFIGURATION.BUNDLE_ID"
                            tooltip={
                                MANUAL_PUBLIC_ID_PLATFORMS.includes(platformName)
                                    ? 'STATIC.PAGES.APPS_CONFIGURATION.BUNDLE_ID_TOOLTIP_PC_CONSOLE'
                                    : 'STATIC.PAGES.APPS_CONFIGURATION.BUNDLE_ID_TOOLTIP_GENERIC'
                            }
                        />
                    }
                    value={site.longname}
                    error={validationErrors.longname}
                    disabled={readOnly}
                    onChange={value => onSitePatch({ longname: value })}
                />
            )}
            {showManualPublicId ? (
                <Field
                    label={
                        <HelpLabel
                            id="STATIC.PAGES.APPS_CONFIGURATION.STORE_ID"
                            tooltip={
                                MANUAL_PUBLIC_ID_PLATFORMS.includes(platformName)
                                    ? 'STATIC.PAGES.APPS_CONFIGURATION.PUBLIC_ID_TOOLTIP_PC_CONSOLE'
                                    : 'STATIC.PAGES.APPS_CONFIGURATION.PUBLIC_ID_TOOLTIP_GENERIC'
                            }
                        />
                    }
                    value={site.public_id}
                    error={validationErrors.url}
                    placeholder="(optional)"
                    disabled={readOnly}
                    onChange={value => onSitePatch({ public_id: value })}
                />
            ) : null}
            {showMappingKey ? (
                <Field
                    label={
                        <HelpLabel
                            id="STATIC.PAGES.APPS_CONFIGURATION.KEYWORD"
                            tooltip="STATIC.PAGES.APPS_CONFIGURATION.MAPPING_KEYS_TOOLTIP"
                        />
                    }
                    value={site.keyword}
                    error={validationErrors.keyword}
                    placeholder="(optional)"
                    disabled={readOnly}
                    minLength={4}
                    maxLength={500}
                    onChange={value => onSitePatch({ keyword: value })}
                />
            ) : null}
            {!showOtherPlatformSelector && displayAsPublished && platformName === OTHER_PLATFORM ? (
                <Field
                    label={<Translate id="STATIC.PAGES.APPS_CONFIGURATION.STORE_ID" />}
                    value={site.public_id}
                    error={validationErrors.url}
                    disabled={readOnly}
                    onChange={value => onSitePatch({ public_id: value })}
                />
            ) : !showOtherPlatformSelector && !showManualPublicId && platformName !== WEB_PLATFORM ? (
                <StaticMetric
                    label={<Translate id="STATIC.PAGES.APPS_CONFIGURATION.STORE_ID" />}
                    value={getPublicIdForDisplay(site.public_id)}
                />
            ) : null}
            {displayAsPublished && !showOtherPlatformSelector && platformName === WEB_PLATFORM ? (
                <Field
                    label={<Translate id="STATIC.PAGES.APPS_CONFIGURATION.BUNDLE_ID" />}
                    value={site.longname}
                    error={validationErrors.longname}
                    disabled={readOnly}
                    onChange={value => onSitePatch({ longname: value })}
                />
            ) : displayAsPublished && !showOtherPlatformSelector ? (
                <StaticMetric
                    label={<Translate id="STATIC.PAGES.APPS_CONFIGURATION.BUNDLE_ID" />}
                    value={getPublicIdForDisplay(site.longname)}
                />
            ) : null}
            {showSdkVersion ? (
                <StaticMetric
                    label={<Translate id="STATIC.PAGES.APPS_CONFIGURATION.LATEST_SDK_VERSION" />}
                    value={site.latest_sdk_version || '-'}
                />
            ) : null}
            {showSdkNotIntegrated ? (
                <StaticMetric
                    label={<Translate id="STATIC.PAGES.APPS_CONFIGURATION.SDK_STATUS" />}
                    value={<Translate id="STATIC.PAGES.APPS_CONFIGURATION.NOT_INTEGRATED" />}
                />
            ) : null}
        </div>
    );
};

SiteIdentityFields.propTypes = {
    displayAsPublished: PropTypes.bool.isRequired,
    onSitePatch: PropTypes.func.isRequired,
    onStoreUrlDraftChange: PropTypes.func.isRequired,
    platformName: PropTypes.string.isRequired,
    platforms: PropTypes.arrayOf(platformPropType).isRequired,
    readOnly: PropTypes.bool.isRequired,
    showManualPublicId: PropTypes.bool.isRequired,
    showMappingKey: PropTypes.bool.isRequired,
    showOtherLatestSdkVersion: PropTypes.bool.isRequired,
    showOtherPlatformSelector: PropTypes.bool.isRequired,
    showSdkNotIntegrated: PropTypes.bool.isRequired,
    showSdkVersion: PropTypes.bool.isRequired,
    site: sitePropType.isRequired,
    storeUrlDraft: PropTypes.string.isRequired,
    validationErrors: PropTypes.objectOf(PropTypes.string).isRequired,
};

export default SiteIdentityFields;
